import React, { useState, useContext, useEffect } from "react"
import "./homepages.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheckCircle, faClock, faBook, faCompass } from "@fortawesome/pro-regular-svg-icons"
import { useParams } from "@reach/router"
import { Link } from "gatsby"
import useSingleProgramDB from "./shared/useSingleProgramDB"
import { FirebaseContext } from "../Firebase"
import LoadingSpinner from "../../components/loading/LoadingSpinner.js"
import { navigate } from "../../link.js"
import { FormattedMessage, useIntl } from "react-intl"
import DOMPurify from "dompurify"
import { ImageWithFallback } from "./views/display.js"

export const ProgramHomeDisplay = ({
  category,
  categoryEs,
  title,
  titleEs,
  description,
  descriptionEs,
  image,
  whyComplete,
  whyCompleteEs,
  bannerColor,
  quote,
  quoteEs,
  quoteAuthor,
  objectives,
  objectivesEs,
  time,
  timeEs,
  programModuleList=[],
  selectedModuleIndex,
  setSelectedModuleIndex,
  handleRestartModule,
  isProgramLoading,
  numModulesCompleted,
  numModulesTotal,
}) => {
  const { locale } = useIntl()

  const renderGradientClassName = () => {
    switch (bannerColor) {
      case "blue":
        return "blue-gradient"
      case "red":
        return "red-gradient"
      case "orange":
        return "orange-gradient"
      case "yellow":
        return "yellow-gradient"
      case "green":
        return "green-gradient"
      case "purple":
        return "purple-gradient"
      default:
        return "blue-gradient"
    }
  }

  if (isProgramLoading) {
    return <LoadingSpinner />
  }

  const localizedQuote = locale === "es-ES" ? quoteEs : quote
  const localizedFullDescription = locale === "es-ES" ? whyCompleteEs : whyComplete
  const localizedCategory = locale === "es-ES" ? categoryEs : category
  const localizedTitle = locale === "es-ES" ? titleEs : title
  const localizedDescription = locale === "es-ES" ? descriptionEs : description
  const localizedSkills = locale === "es-ES" ? objectivesEs : objectives
  const localizedModules = programModuleList.map(module => {
    const localizedModule = {
      status: module.status,
      module_id: module.module_id,
      program_id: module.program_id,
    }
    const module_json = module.module_json || {}
    if (locale === "es-ES") {
      localizedModule.title = module_json.titleEs || module_json.title
      localizedModule.time = module_json.timeEs || module_json.time
      localizedModule.whatLearn = module_json.whatLearnEs || module_json.whatLearn
      localizedModule.directions = module_json.directionsEs || module_json.directions
    } else {
      localizedModule.title = module_json.title
      localizedModule.time = module_json.time
      localizedModule.whatLearn = module_json.whatLearn
      localizedModule.directions = module_json.directions
    }
    return localizedModule
  })

  const upNextModule = localizedModules[localizedModules?.findIndex((module) => module.status !== "completed")] || null
  const selectedModule = localizedModules[selectedModuleIndex || 0] || null

  // none are in_progress or completed, so we can assume the program has not started
  const hasProgramStarted = localizedModules.some((module) => module.status === "in_progress" || module.status === "completed")
  
  return (
    <div className="curriculum-home program">
      <div className={`home-banner ${renderGradientClassName()}`}>
        <div className="inner">
          <div className="home-container">
            <div className="home-header">
              <label className="home-label">{localizedCategory}</label>
              <h2 className="home-title">{localizedTitle}</h2>
              <p className="home-description">{localizedDescription}</p>
              <ImageWithFallback className="home-image" src={image} />
            </div>

            <div className="home-cta">
              {localizedModules && upNextModule ? (
                <>
                  <div style={{ marginBottom: "1rem", width: '100%' }}>
                    <label>
                      {hasProgramStarted ? (
                        <FormattedMessage id="curriculum-up-next" defaultMessage="You Can Do It!" />
                      ) : (
                        <FormattedMessage id="curriculum-get-started" defaultMessage="Get Started" />
                      )}
                    </label>{" "}
                    <h6>{localizedModules[localizedModules?.findIndex((module) => module.status !== "completed")]?.title}</h6>
                  </div>
                  <Link
                    to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${
                      localizedModules[localizedModules?.findIndex((module) => module.status !== "completed")]?.module_id
                    }/interactive?program=${localizedModules[localizedModules?.findIndex((module) => module.status !== "completed")]?.program_id}`}
                  >
                    <button className="button component-button component-button-default" style={{ padding: "12px 24px"}}>
                      {localizedModules[localizedModules?.findIndex((module) => module.status !== "completed")]?.status === "in_progress"
                        ? <FormattedMessage id="curriculum-continue" defaultMessage="Continue" />
                        : <FormattedMessage id="curriculum-start-module" defaultMessage="Get Started" />}
                    </button>
                  </Link>
                </>
              ) : (
                <>
                  <h4><FormattedMessage id="curriculum-program-completed" defaultMessage="Program Completed" /></h4>
                  <p><FormattedMessage id="curriculum-congratulations-on-completing-all-modules" defaultMessage="Congratulations on completing all modules!" /></p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="home-container inner">
        <div className="home-content">
          <div className="home-why-complete">
            <h3>
              <FormattedMessage id="curriculum-why-complete-this-program" defaultMessage="Why complete this program" />
            </h3>
            {!!localizedQuote && (
              <>
                <blockquote>"{localizedQuote}"</blockquote>
                {quoteAuthor && <label>-{quoteAuthor}</label>}
              </>
            )}
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedFullDescription) }} />
          </div>
          {localizedSkills?.length > 0 && (
            <div className="home-skills">
              <h6>
                <FormattedMessage id="curriculum-skills-you-will-develop" defaultMessage="Skills you will develop:" />
              </h6>
              <div className="home-skills-list">
                {localizedSkills.map((skill, index) => (
                  <div key={index} className="home-skill">
                    {skill}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="home-tasks">
          <div className="home-tasks-header">
            <h3><FormattedMessage id="curriculum-section-overview" defaultMessage="Section overview" /></h3>
            {/* <label>
              {" "}
              {numModulesCompleted} / {numModulesTotal} Complete
            </label> */}
          </div>
          <div className="home-tasks-container">
            <aside className="home-tasks-aside">
              {localizedModules &&
                localizedModules.map((module, index) => (
                  <div
                    key={index}
                    className={`home-task ${selectedModuleIndex === index ? "selected" : ""}`}
                    onClick={() => setSelectedModuleIndex(index)}
                  >
                    {module.status === "completed" ? (
                      <div className="home-task-number">
                        <FontAwesomeIcon icon={faCheckCircle} color="#63C0D5" size="lg" />
                      </div>
                    ) : (
                      <div className="home-task-number">{index + 1}</div>
                    )}
                    <div>{module.title}</div>
                  </div>
                ))}
            </aside>
            <div className="home-task-content">
              <div className="home-task-content-inner">
                <h5>
                  <FormattedMessage id="curriculum-overview" defaultMessage="Overview" />
                </h5>
                {localizedModules && selectedModule && (
                  <>
                    {" "}
                    {selectedModule?.time && (
                      <span>
                        {" "}
                        <FontAwesomeIcon icon={faClock} color="#63C0D5" />
                        {selectedModule?.time}
                      </span>
                    )}
                  
                      {/* <p>{selectedModule?.description}</p> */}

                      <div className="home-task-overview">
                        <div className="home-task-overview-icon">
                          <FontAwesomeIcon icon={faBook} color="#63C0D5" size="2x" />
                        </div>
                        <div>
                          <h5>
                            <FormattedMessage id="curriculum-what-you-will-learn" defaultMessage="What you will learn" />
                          </h5>
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedModule.whatLearn) }} />
                        </div>
                      </div>
                      <div className="home-task-overview" style={{ marginTop: "1rem" }}>
                        <div className="home-task-overview-icon">
                          <FontAwesomeIcon icon={faCompass} color="#63C0D5" size="2x" />
                        </div>
                        <div>
                          <h5>
                            <FormattedMessage id="curriculum-directions" defaultMessage="Directions" />
                          </h5>
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedModule.directions) }} />
                        </div>
                      </div>

                      {selectedModule?.status === "completed" ? (
                        <a style={{textAlign: "right"}}>
                        <button
                          style={{ marginTop: "1rem" }}
                          onClick={() => handleRestartModule(selectedModule?.module_id)}
                          className="button component-button component-button-ghost component-button-small"
                        >
                          <FormattedMessage id="curriculum-restart" defaultMessage="Restart" />
                        </button>
                        </a>
                      ) : (
                        <Link to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${selectedModule?.module_id}/interactive?program=${selectedModule?.program_id}`} style={{textAlign: "right"}}>
                          <button className="button component-button component-button-default component-button-small" style={{ marginTop: "1rem" }}>
                            {selectedModule?.status === "in_progress" ? <FormattedMessage id="curriculum-continue" defaultMessage="Continue" /> : <FormattedMessage id="curriculum-start" defaultMessage="Start" /> }{" "}
                          </button>
                        </Link>
                      )}
                  
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const ProgramHome = () => {
  const [selectedModuleIndex, setSelectedModuleIndex] = useState(0)
  const { programId } = useParams()

  const { firebase } = useContext(FirebaseContext)
  const { programData, programModuleList = [], isProgramLoading } = useSingleProgramDB(programId, firebase)

  const { category = "", category_es, title, title_es, objectives = [], objectives_es = [], quote, quote_es, description, description_es, why_complete, why_complete_es, image, quote_author, banner_color } = programData || {}

  const numModulesTotal = programModuleList.length
  const numModulesCompleted = programModuleList.filter((module) => module.status === "completed").length

  const handleRestartModule = async (moduleId) => {
    await firebase.restartUserModule(moduleId)
    navigate(
      `/modules/${programModuleList[selectedModuleIndex]?.module_id}/interactive?program=${programModuleList[selectedModuleIndex]?.program_id}`
    )
  }

  useEffect(() => {
    if (programModuleList.length > 0) {
      const completedModuleIndex = programModuleList.findIndex((module) => module.status !== "completed")

      completedModuleIndex === -1 ? setSelectedModuleIndex(0) : setSelectedModuleIndex(completedModuleIndex)
    }
  }, [programModuleList])

  if (isProgramLoading) {
    return <LoadingSpinner />
  }

  return (
    <ProgramHomeDisplay
      category={category}
      categoryEs={category_es}
      title={title}
      titleEs={title_es}
      description={description}
      descriptionEs={description_es}
      image={image}
      whyComplete={why_complete}
      whyCompleteEs={why_complete_es}
      objectives={objectives}
      objectivesEs={objectives_es}
      programModuleList={programModuleList}
      selectedModuleIndex={selectedModuleIndex}
      setSelectedModuleIndex={setSelectedModuleIndex}
      quote={quote}
      quoteEs={quote_es}
      quoteAuthor={quote_author}
      bannerColor={banner_color}
      handleRestartModule={handleRestartModule}
      isProgramLoading={isProgramLoading}
      numModulesCompleted={numModulesCompleted}
      numModulesTotal={numModulesTotal}
    />
  )
}
